import { LegalBeneficiary } from 'src/models/helperTypes/EmissionDataFields';
import relationships from 'src/constants/relationships';
import { FormHookResult } from 'design-system';
import { MAX_BENEFICIARIES } from './makeBeneficiariesValidationSchema';
import FormShape from './FormShape';

export function findRelationName(value: string): string {
  const help = relationships.find((x) => x.Value === value);
  return help ? (help.Text as string) : '';
}

export function parseData(data: FormShape): LegalBeneficiary[] {
  const helper = new Array(data.totalBeneficiaries).fill(0);
  const beneficiaries: LegalBeneficiary[] = [];
  helper.forEach((_, index) => {
    const relationship = data[
      `beneficiary${index}relationship` as keyof FormShape
    ] as string;

    const beneficiary: LegalBeneficiary = {
      firstName: data[
        `beneficiary${index}firstName` as keyof FormShape
      ] as string,
      lastName: data[
        `beneficiary${index}lastName` as keyof FormShape
      ] as string,
      gender: data[`beneficiary${index}gender` as keyof FormShape] as string,
      birthday: data[
        `beneficiary${index}birthday` as keyof FormShape
      ] as string,
      nationality: data[
        `beneficiary${index}nationality` as keyof FormShape
      ] as string,
      nationalityName: data[
        `beneficiary${index}nationalityName` as keyof FormShape
      ] as string,
      civilStatus: data[
        `beneficiary${index}civilStatus` as keyof FormShape
      ] as string,
      civilStatusName: data[
        `beneficiary${index}civilStatusName` as keyof FormShape
      ] as string,
      idType: data[`beneficiary${index}idType` as keyof FormShape] as string,
      id: data[`beneficiary${index}id` as keyof FormShape] as string,
      participationRate: data[
        `beneficiary${index}participationRate` as keyof FormShape
      ] as string,
      relationship,
      relationshipName: findRelationName(relationship),
    };
    beneficiaries.push(beneficiary);
  });

  return beneficiaries;
}

export function formatInicialData(
  data: LegalBeneficiary[] | undefined,
): LegalBeneficiary[] {
  return data || [];
}

export function parseInitialFormData(
  initialData: LegalBeneficiary[] | undefined,
): FormShape {
  const beneficiaryData: { [x: string]: string } = {};
  const helper = new Array(MAX_BENEFICIARIES).fill(0);
  helper.forEach((_, index) => {
    beneficiaryData[`beneficiary${index}firstName`] = '';
    beneficiaryData[`beneficiary${index}lastName`] = '';
    beneficiaryData[`beneficiary${index}gender`] = 'F';
    beneficiaryData[`beneficiary${index}birthday`] = '';
    beneficiaryData[`beneficiary${index}nationality`] = '';
    beneficiaryData[`beneficiary${index}nationalityName`] = '';
    beneficiaryData[`beneficiary${index}civilStatus`] = '';
    beneficiaryData[`beneficiary${index}civilStatusName`] = '';
    beneficiaryData[`beneficiary${index}idType`] = 'N';
    beneficiaryData[`beneficiary${index}id`] = '';
    beneficiaryData[`beneficiary${index}relationship`] = '';
    beneficiaryData[`beneficiary${index}participationRate`] = '';
  });

  const data = formatInicialData(initialData);

  data.forEach((beneficiary, index) => {
    beneficiaryData[`beneficiary${index}firstName`] = beneficiary.firstName;
    beneficiaryData[`beneficiary${index}lastName`] = beneficiary.lastName;
    beneficiaryData[`beneficiary${index}gender`] = beneficiary.gender;
    beneficiaryData[`beneficiary${index}birthday`] = beneficiary.birthday;
    beneficiaryData[`beneficiary${index}nationality`] = beneficiary.nationality;
    beneficiaryData[`beneficiary${index}nationalityName`] =
      beneficiary.nationalityName;
    beneficiaryData[`beneficiary${index}civilStatus`] = beneficiary.civilStatus;
    beneficiaryData[`beneficiary${index}civilStatusName`] =
      beneficiary.civilStatusName;
    beneficiaryData[`beneficiary${index}idType`] = beneficiary.idType;
    beneficiaryData[`beneficiary${index}id`] = beneficiary.id;
    beneficiaryData[`beneficiary${index}relationship`] =
      beneficiary.relationship;
    beneficiaryData[`beneficiary${index}participationRate`] =
      beneficiary.participationRate;
  });

  const formFields = {
    totalBeneficiaries: Math.max(data.length, 1),
    ...beneficiaryData,
  };

  return formFields as unknown as FormShape;
}

function createLegalBeneficiaryFromIndex(
  getInputProps: FormHookResult,
  index: number,
): LegalBeneficiary {
  return {
    firstName: getInputProps(`beneficiary${index}firstName`).value as string,
    lastName: getInputProps(`beneficiary${index}lastName`).value as string,
    gender: getInputProps(`beneficiary${index}gender`).value as string,
    birthday: getInputProps(`beneficiary${index}birthday`).value as string,
    nationality: getInputProps(`beneficiary${index}nationality`)
      .value as string,
    nationalityName: getInputProps(`beneficiary${index}nationalityName`)
      .value as string,
    civilStatus: getInputProps(`beneficiary${index}civilStatus`)
      .value as string,
    civilStatusName: getInputProps(`beneficiary${index}civilStatusName`)
      .value as string,
    idType: getInputProps(`beneficiary${index}idType`).value as string,
    id: getInputProps(`beneficiary${index}id`).value as string,
    relationship: getInputProps(`beneficiary${index}relationship`)
      .value as string,
    participationRate: getInputProps(`beneficiary${index}participationRate`)
      .value as string,
    relationshipName: '',
  };
}

export function deleteBenefiactyDataOnForm(
  getInputProps: FormHookResult,
  deletedItemIndex: number,
): FormShape {
  const totalBeneficiaries = getInputProps('totalBeneficiaries')
    .value as number;
  const helper = new Array(totalBeneficiaries).fill(0);
  const currentBeneficiaryData: LegalBeneficiary[] = [];
  helper.forEach((_, index) => {
    currentBeneficiaryData.push(
      createLegalBeneficiaryFromIndex(getInputProps, index),
    );
  });
  currentBeneficiaryData.splice(deletedItemIndex, 1);
  return parseInitialFormData(currentBeneficiaryData);
}
